<template>
  <div class="project-title-combo">
    <a-auto-complete
      :value="modelValue"
      :options="displayOptions"
      :placeholder="placeholderText"
      :disabled="disabled"
      :maxlength="100"
      allow-clear
      class="project-title-combo__input"
      @focus="handleFocus"
      @search="handleSearch"
      @select="handleSelect"
      @change="handleChange"
    >
      <template #notFoundContent>
        <div v-if="loading" class="combo-state combo-state--loading">
          <a-spin size="small" />
          <span>{{ t('project.titleCombo.loading') }}</span>
        </div>
        <div v-else-if="loadError" class="combo-state combo-state--error">
          {{ loadError }}
          <a-button type="link" size="small" @click="loadProjects">
            {{ t('project.titleCombo.retry') }}
          </a-button>
        </div>
        <div v-else class="combo-state combo-state--empty">
          {{ t('project.titleCombo.empty') }}
        </div>
      </template>
    </a-auto-complete>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { getProjects, type Project } from '@/modules/project/api/project'
import { useUserStore } from '@/modules/user/stores'

const props = withDefaults(
  defineProps<{
    modelValue: string
    /** 点选下拉后锁定的文件夹 project_id */
    projectId?: number | null
    disabled?: boolean
    placeholder?: string
  }>(),
  {
    projectId: null,
    disabled: false,
    placeholder: undefined,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'update:projectId': [value: number | null]
}>()

const { t } = useI18n()
const userStore = useUserStore()

const loading = ref(false)
const loadError = ref<string | null>(null)
const projects = ref<Project[]>([])
const searchText = ref('')
const loaded = ref(false)

const placeholderText = computed(
  () => props.placeholder || t('project.titleCombo.placeholder'),
)

const filteredProjects = computed(() => {
  const q = searchText.value.trim().toLowerCase()
  if (!q) return projects.value
  return projects.value.filter((p) => p.title.toLowerCase().includes(q))
})

const displayOptions = computed(() =>
  filteredProjects.value.map((p) => ({
    value: p.title,
    label: p.title,
    key: String(p.id),
  })),
)

function sortByRecentUpdate(items: Project[]): Project[] {
  return [...items].sort((a, b) => {
    const ta = a.updated_at ? Date.parse(a.updated_at) : 0
    const tb = b.updated_at ? Date.parse(b.updated_at) : 0
    return tb - ta
  })
}

async function loadProjects() {
  try {
    loading.value = true
    loadError.value = null
    const response = await getProjects()
    const currentUserId = userStore.userInfo?.id
    const owned = currentUserId
      ? response.items.filter((p) => p.creator_id === currentUserId)
      : response.items
    projects.value = sortByRecentUpdate(owned)
    loaded.value = true
  } catch (error) {
    console.error('Failed to load projects for title combo:', error)
    loadError.value = t('project.titleCombo.loadFailed')
  } finally {
    loading.value = false
  }
}

async function handleFocus() {
  searchText.value = props.modelValue || ''
  if (!loaded.value && !loading.value) {
    await loadProjects()
  }
}

function maybeUnlockProject(value: string) {
  if (props.projectId != null) {
    const locked = projects.value.find((p) => p.id === props.projectId)
    if (!locked || locked.title !== value) {
      emit('update:projectId', null)
    }
  }
}

function handleSearch(value: string) {
  searchText.value = value
  emit('update:modelValue', value)
  maybeUnlockProject(value)
}

function handleChange(value: string) {
  const next = value ?? ''
  emit('update:modelValue', next)
  searchText.value = next
  maybeUnlockProject(next)
}

function handleSelect(value: string) {
  const match = projects.value.find((p) => p.title === value)
  emit('update:modelValue', value)
  searchText.value = value
  emit('update:projectId', match?.id ?? null)
}

defineExpose({
  loadProjects,
  reload: loadProjects,
})
</script>

<style scoped>
.project-title-combo__input {
  width: 100%;
}

.combo-state {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  color: #8c8c8c;
  font-size: 13px;
}

.combo-state--error {
  color: #ff4d4f;
  flex-wrap: wrap;
}

.combo-state--empty {
  justify-content: center;
}
</style>
